import { ColumnDropdownOption, EntityConditionOptions, InsightsDropdownGroup } from '../types'

/**
 * Narrows a column catalog to the options the current report context may use, keeping each
 * group in alphabetical order and discarding groups left with nothing in them.
 *
 * `retainedColumnValues` grandfathers columns that are already part of a report's saved
 * selection. Conditions gate what a user may *add*; tightening a condition must never make an
 * existing saved report unreadable, so a column already selected stays listed - visible in the
 * picker, still rendered in the table, and removable by the user - even though the report could
 * no longer add it from scratch. Callers that intentionally prune stale selections (for example
 * when the breakdown changes and its dimension columns no longer apply) pass nothing here and
 * get the strict list - see {@link pruneColumnsInvalidatedByBreakdown} for how the two are
 * kept apart.
 */
export function resolveAvailableColumnOptions(
    optionCategories: Array<InsightsDropdownGroup<ColumnDropdownOption>>,
    conditionOptions: EntityConditionOptions,
    retainedColumnValues: string[] = [],
): Array<InsightsDropdownGroup<ColumnDropdownOption>> {
    const retained = new Set(retainedColumnValues)

    return optionCategories
        .map((category) => {
            const availableOptions = category.options.filter((option) => {
                if (retained.has(option.value)) return true

                return option.condition?.(conditionOptions) ?? true
            })

            const alphabeticalOptions = [...availableOptions].sort((a, b) => {
                const aLabel = (a.label || a.value).toLowerCase()
                const bLabel = (b.label || b.value).toLowerCase()
                return aLabel > bLabel ? 1 : aLabel < bLabel ? -1 : 0
            })

            return {
                ...category,
                // Keep defaults as-is, but present each group in predictable alpha order.
                options: alphabeticalOptions,
            }
        })
        .filter((category) => category.options.length > 0)
}

/**
 * Drops the selected columns a breakdown change has invalidated - and only those.
 *
 * When a selected column is not offered under the new breakdown it is failing for one of two
 * unrelated reasons, and they call for opposite handling:
 *
 *  - It is a dimension of a breakdown the report no longer uses (Variant, Step, Button...).
 *    Some other breakdown does offer it, so the breakdown change is what invalidated it. Left
 *    in place it renders as an empty phantom column, so it is dropped.
 *  - No breakdown offers it, because a condition that has nothing to do with the breakdown
 *    withholds it - the report selected it before that condition existed. Dropping it here
 *    would rewrite a selection the user never edited, and the next save would persist the
 *    loss, so it is kept exactly as {@link resolveAvailableColumnOptions} keeps it listed.
 *
 * Both column sets must come from the strict (non-grandfathered) catalog, or every column
 * looks breakdown-independent and nothing is ever pruned.
 */
export function pruneColumnsInvalidatedByBreakdown(
    selectedColumnValues: string[],
    columnValuesOfferedByNewBreakdown: Iterable<string>,
    columnValuesOfferedBySomeBreakdown: Iterable<string>,
): string[] {
    const offeredNow = new Set(columnValuesOfferedByNewBreakdown)
    const breakdownDependent = new Set(columnValuesOfferedBySomeBreakdown)

    return selectedColumnValues.filter((value) => offeredNow.has(value) || !breakdownDependent.has(value))
}
